import React, { useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Drawer, Form, Select, InputNumber, Space, message } from 'antd';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchSqlData, insertData, updateData } from '../../../services/api';
import { useTranslation } from 'react-i18next';
import StandardTable, { createActionColumn } from '../../../components/common/StandardTable';
import FilterBar from '../../../components/common/FilterBar';
import Button from '../../../components/common/Button';

const { Title } = Typography;

interface MaintRule {
  id: number;
  maintenance_type_id: number;
  interval_km?: number | null;
  interval_hours?: number | null;
  interval_days?: number | null;
  warning_threshold_km?: number | null;
  warning_threshold_hours?: number | null;
  warning_threshold_days?: number | null;
}
interface MaintType {
  id: number;
  name: string;
}

const MachMaintenanceRules: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: types = [] } = useQuery<MaintType[], Error>({
    queryKey: ['adm_maintenance_types'],
    queryFn: () => fetchSqlData('adm_maintenance_types'),
    staleTime: 10 * 60_000,
  });
  const { data: rules = [], isLoading } = useQuery<MaintRule[], Error>({
    queryKey: ['adm_maintenance_rules'],
    queryFn: () => fetchSqlData('adm_maintenance_rules'),
    staleTime: 10 * 60_000,
  });

  const typeMap = useMemo(() => {
    const m: Record<number, string> = {};
    (types || []).forEach(t => { if (t.id) m[t.id] = t.name; });
    return m;
  }, [types]);
  const typeOptions = (types || []).map(t => ({ value: t.id, label: t.name }));

  const [typeFilter, setTypeFilter] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    let arr = rules;
    if (typeFilter != null) {
      arr = arr.filter(r => Number(r.maintenance_type_id) === Number(typeFilter));
    }
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      arr = arr.filter(r =>
        (typeMap[r.maintenance_type_id] || '').toLowerCase().includes(s)
      );
    }
    return arr;
  }, [rules, typeFilter, search, typeMap]);

  const columns = [
    { title: t('machinery.maintenance.common.maintenanceType'), dataIndex: 'maintenance_type_id', key: 'maintenance_type_id', render: (id: number) => typeMap[id] || id, width: 220 },
    { title: t('machinery.maintenance.rules.intervalKm'), dataIndex: 'interval_km', key: 'interval_km', align: 'right' as const, width: 120 },
    { title: t('machinery.maintenance.rules.intervalHours'), dataIndex: 'interval_hours', key: 'interval_hours', align: 'right' as const, width: 140 },
    { title: t('machinery.maintenance.rules.intervalDays'), dataIndex: 'interval_days', key: 'interval_days', align: 'right' as const, width: 140 },
    { title: t('machinery.maintenance.rules.warningKm'), dataIndex: 'warning_threshold_km', key: 'warning_threshold_km', align: 'right' as const, width: 120 },
    { title: t('machinery.maintenance.rules.warningHours'), dataIndex: 'warning_threshold_hours', key: 'warning_threshold_hours', align: 'right' as const, width: 140 },
    { title: t('machinery.maintenance.rules.warningDays'), dataIndex: 'warning_threshold_days', key: 'warning_threshold_days', align: 'right' as const, width: 140 },
  ];

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<MaintRule | null>(null);
  const [form] = Form.useForm();

  const handleAdd = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };
  const handleEdit = (r: MaintRule) => {
    setEditing(r);
    form.setFieldsValue(r);
    setOpen(true);
  };
  const handleSubmit = async () => {
    try {
      const v = await form.validateFields();
      const hasInterval = [v.interval_km, v.interval_hours, v.interval_days].some((value: number | null | undefined) => value != null);
      if (!hasInterval) {
        message.error(t('machinery.maintenance.rules.minOneInterval'));
        return;
      }

      if (v.warning_threshold_km != null && v.interval_km != null && Number(v.warning_threshold_km) > Number(v.interval_km)) {
        message.error(t('machinery.maintenance.rules.warningKmTooHigh'));
        return;
      }
      if (v.warning_threshold_hours != null && v.interval_hours != null && Number(v.warning_threshold_hours) > Number(v.interval_hours)) {
        message.error(t('machinery.maintenance.rules.warningHoursTooHigh'));
        return;
      }
      if (v.warning_threshold_days != null && v.interval_days != null && Number(v.warning_threshold_days) > Number(v.interval_days)) {
        message.error(t('machinery.maintenance.rules.warningDaysTooHigh'));
        return;
      }

      const hasDuplicate = rules.find(rule =>
        rule.id !== editing?.id &&
        Number(rule.maintenance_type_id) === Number(v.maintenance_type_id)
      );
      if (hasDuplicate) {
        message.error(t('machinery.maintenance.rules.duplicateRule'));
        return;
      }

      const payload: Partial<MaintRule> = {
        maintenance_type_id: Number(v.maintenance_type_id),
        interval_km: v.interval_km ?? null,
        interval_hours: v.interval_hours ?? null,
        interval_days: v.interval_days ?? null,
        warning_threshold_km: v.warning_threshold_km ?? null,
        warning_threshold_hours: v.warning_threshold_hours ?? null,
        warning_threshold_days: v.warning_threshold_days ?? null,
      };
      if (editing?.id) {
        await updateData('adm_maintenance_rules', editing.id, payload);
        message.success(t('machinery.maintenance.rules.updateSuccess'));
      } else {
        await insertData('adm_maintenance_rules', payload);
        message.success(t('machinery.maintenance.rules.addSuccess'));
      }
      setOpen(false);
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ['adm_maintenance_rules'] });
    } catch (e: any) {
      message.error(e?.message || t('machinery.maintenance.common.operationFailed'));
    }
  };

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <SettingOutlined style={{ marginRight: 8 }} />
            {t('machinery.maintenance.rules.title')}
          </Title>
        </Col>
        <Col>
          <Button action="add" type="primary" icon={<PlusOutlined />} onClick={handleAdd}>{t('machinery.maintenance.rules.addButton')}</Button>
        </Col>
      </Row>

      <FilterBar
        items={[
          { type: 'text', key: 'search', placeholder: t('machinery.maintenance.rules.searchPlaceholder'), value: search, onChange: v => setSearch(v), width: 240 },
          { type: 'select', key: 'type', placeholder: t('machinery.maintenance.rules.typePlaceholder'), value: typeFilter as any, onChange: v => setTypeFilter(v), options: typeOptions, width: 280, allowClear: true, showSearch: true },
        ]}
        onReset={() => { setTypeFilter(undefined); setSearch(''); }}
      />

      <StandardTable
        rowKey="id"
        columns={[
          ...columns,
          createActionColumn<MaintRule>({
            onEdit: handleEdit,
            showView: false,
            showDelete: false,
            align: 'left',
          }) as any,
        ] as any}
        dataSource={filtered}
        loading={isLoading}
        bordered
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />

      <Drawer title={editing ? t('machinery.maintenance.rules.editTitle') : t('machinery.maintenance.rules.addTitle')} open={open} onClose={() => setOpen(false)} width={520} destroyOnClose>
        <Form layout="vertical" form={form}>
          <Form.Item name="maintenance_type_id" label={t('machinery.maintenance.common.maintenanceType')} rules={[{ required: true, message: t('machinery.maintenance.rules.typeRequired') }]}>
            <Select options={typeOptions} showSearch placeholder={t('machinery.maintenance.rules.selectPlaceholder')} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="interval_km" label={t('machinery.maintenance.rules.intervalKm')}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="interval_hours" label={t('machinery.maintenance.rules.intervalHours')}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="interval_days" label={t('machinery.maintenance.rules.intervalDays')}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="warning_threshold_km" label={t('machinery.maintenance.rules.warningKm')}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="warning_threshold_hours" label={t('machinery.maintenance.rules.warningHours')}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="warning_threshold_days" label={t('machinery.maintenance.rules.warningDays')}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setOpen(false)}>{t('machinery.machines.common.cancel')}</Button>
            <Button action={editing ? 'edit' : 'add'} type="primary" onClick={handleSubmit}>{t('machinery.machines.common.save')}</Button>
          </Space>
        </Form>
      </Drawer>
    </Card>
  );
};

export default MachMaintenanceRules;

